import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    Divider,
    IconButton,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useAuth } from "auth/Auth";
import colors from "assets/theme/base/colors";

const packageLabel = (option) => {
    if (!option) return "";
    if (option.title) return option.title;
    const tripTitle = option.trips?.[0]?.title;
    if (tripTitle) return `${tripTitle} (#${option.id})`;
    return `Untitled Package (#${option.id})`;
};

const resolveTripId = (pkg) => {
    if (!pkg) return null;
    if (pkg.trip_id) return pkg.trip_id;
    const fromTrips = pkg.trips?.[0]?.id;
    return fromTrips || null;
};

const TripAddDialog = ({ open, onClose, dialogTitle, wizardStep }) => {
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();

    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [loadingPackages, setLoadingPackages] = useState(false);
    const [proceeding, setProceeding] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) {
            setSelectedPackage(null);
            setProceeding(false);
            setError("");
            return;
        }

        const fetchPackages = async () => {
            setLoadingPackages(true);
            setError("");
            try {
                const response = await axiosInstance.get("/trip-packages");
                setPackages(response.data || []);
            } catch (err) {
                console.error("Error fetching packages:", err);
                setPackages([]);
                const detail =
                    err?.response?.data?.detail ||
                    err?.response?.data?.message ||
                    err?.message;
                setError(
                    typeof detail === "string" && detail.trim()
                        ? detail
                        : "Could not load packages. Please try again."
                );
            } finally {
                setLoadingPackages(false);
            }
        };

        fetchPackages();
    }, [open, axiosInstance]);

    const handleProceed = () => {
        setError("");

        if (!selectedPackage?.id) {
            setError("Please select a package to continue.");
            return;
        }

        const tripId = resolveTripId(selectedPackage);
        if (!tripId) {
            setError(
                "This package isn't linked to a trip yet. Open the package and link it to a trip before adding trip logistics."
            );
            return;
        }

        setProceeding(true);
        try {
            navigate(
                `/trips/${tripId}/packages/${selectedPackage.id}/edit?step=${wizardStep}`
            );
            onClose();
        } catch (err) {
            console.error("Error opening trip logistics wizard:", err);
            setError("Something went wrong opening the editor. Please try again.");
            setProceeding(false);
        }
    };

    const handleClose = () => {
        if (proceeding) return;
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: "100%",
                    maxWidth: 420,
                    m: 2,
                    borderRadius: "16px",
                    boxShadow: "0 20px 48px rgba(14, 35, 72, 0.16)",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.5,
                    pt: 1.5,
                    pb: 1.25,
                }}
            >
                <Box
                    component="span"
                    sx={{
                        fontFamily: '"Sora", "Roboto", sans-serif',
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        color: colors.dark.main,
                        lineHeight: 1.3,
                    }}
                >
                    {dialogTitle}
                </Box>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    disabled={proceeding}
                    aria-label="Close"
                    sx={{
                        width: 30,
                        height: 30,
                        border: `1px solid ${colors.grey[400]}`,
                        borderRadius: "8px",
                        color: colors.text.main,
                        "&:hover": { bgcolor: colors.grey[200] },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            <Divider sx={{ borderColor: colors.grey[300], m: 0 }} />

            {/* Body */}
            <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
                <Box
                    component="span"
                    sx={{
                        display: "block",
                        fontSize: "0.84rem",
                        fontWeight: 500,
                        color: colors.primary.main,
                        mb: 2,
                    }}
                >
                    Select an existing package to configure.
                </Box>

                {error ? (
                    <Alert
                        severity="error"
                        sx={{ mb: 2, borderRadius: "10px", fontSize: "0.82rem" }}
                        onClose={() => setError("")}
                    >
                        {error}
                    </Alert>
                ) : null}

                <Box
                    component="span"
                    sx={{
                        display: "block",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: colors.text.main,
                        mb: 0.75,
                    }}
                >
                    Select Package <Box component="span" sx={{ color: "error.main" }}>*</Box>
                </Box>

                <Autocomplete
                    options={packages}
                    loading={loadingPackages}
                    value={selectedPackage}
                    onChange={(_, value) => {
                        setSelectedPackage(value);
                        setError("");
                    }}
                    getOptionLabel={packageLabel}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={proceeding}
                    noOptionsText={
                        loadingPackages
                            ? "Loading packages..."
                            : "No packages found"
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search or select package..."
                            size="small"
                            fullWidth
                            error={Boolean(error) && !selectedPackage}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingPackages ? (
                                            <CircularProgress color="inherit" size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "11px",
                                    fontSize: "0.875rem",
                                    "&:hover fieldset": { borderColor: colors.primary.main },
                                    "&.Mui-focused fieldset": {
                                        borderColor: colors.primary.main,
                                        boxShadow: `0 0 0 3px ${colors.brand.primaryContainer}`,
                                    },
                                },
                            }}
                        />
                    )}
                />
            </Box>

            <Divider sx={{ borderColor: colors.grey[300], m: 0 }} />

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 1,
                    px: 2.5,
                    pt: 1.25,
                    pb: 1.5,
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={proceeding}
                    sx={{
                        minWidth: 92,
                        py: 0.85,
                        px: 2,
                        borderRadius: "999px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.84rem",
                        color: colors.text.main,
                        border: `1px solid ${colors.grey[400]}`,
                        bgcolor: colors.white.main,
                        "&:hover": {
                            bgcolor: colors.grey[200],
                            borderColor: colors.grey[500],
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleProceed}
                    disabled={!selectedPackage || loadingPackages || proceeding}
                    sx={{
                        minWidth: 108,
                        py: 0.85,
                        px: 2.5,
                        borderRadius: "999px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.84rem",
                        color: colors.white.main,
                        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.dark.focus} 100%)`,
                        boxShadow: "0 6px 16px rgba(21, 101, 192, 0.28)",
                        "&:hover": {
                            color: colors.white.main,
                            background: `linear-gradient(135deg, ${colors.primary.focus} 0%, ${colors.primary.main} 100%)`,
                            boxShadow: "0 8px 20px rgba(21, 101, 192, 0.32)",
                        },
                        "&:disabled": {
                            color: colors.grey[600],
                            background: colors.grey[300],
                            boxShadow: "none",
                        },
                    }}
                >
                    {proceeding ? (
                        <CircularProgress size={18} color="inherit" />
                    ) : (
                        "Proceed"
                    )}
                </Button>
            </Box>
        </Dialog>
    );
};

TripAddDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    dialogTitle: PropTypes.string.isRequired,
    wizardStep: PropTypes.string.isRequired,
};

export default TripAddDialog;
